import type { CoverageRow } from '@fruuit/shared'
import {
  Button,
  Callout,
  Card,
  DataTable,
  EmptyState,
  Input,
  PageHeader,
  Select,
  StatusBadge,
  Tabs,
  useDialog,
} from '@fruuit/ui'
import { Building, Upload } from 'lucide-react'
import { useState } from 'react'
import { useParams, useSearchParams } from 'react-router'
import { errorMessage } from '../../api/client'
import { useSession } from '../../auth/session-context'
import { CompanyDossierPage } from '../../features/company/CompanyDossier'
import { useCoverageClients } from '../../features/coverage/api'
import { CoverageRegister } from '../../features/coverage/CoverageRegister'
import { formatDate } from '../../features/coverage/format'
import { useHoldings, usePortfolios } from '../../features/holdings/api'
import { ImportDialog } from '../../features/holdings/ImportDialog'
import { MappingExceptionsTab } from '../../features/holdings/MappingExceptions'
import { useTitle } from '../../lib/useTitle'

/**
 * Workspace Coverage (S2 team view, I-10): an explicit client context
 * (P-ID-013), the coverage register with staff columns, the mapping-exception
 * queue and the client's holdings with CSV import.
 */
export function CoveragePage() {
  useTitle('Coverage')
  const [params, setParams] = useSearchParams()
  const org = params.get('org')
  const tab = params.get('tab') ?? 'coverage'
  const clients = useCoverageClients()
  const me = useSession()
  const manager = me.memberships.some((m) => m.role === 'manager')
  const [focusException, setFocusException] = useState<string | null>(null)
  const client = clients.data?.items.find((c) => c.id === org)

  const setParam = (key: string, value: string | null) =>
    setParams((current) => {
      const next = new URLSearchParams(key === 'org' ? '' : current)
      if (key !== 'org' && org) next.set('org', org)
      if (value) next.set(key, value)
      else next.delete(key)
      return next
    })

  return (
    <>
      <PageHeader
        eyebrow="Coverage"
        title={client ? client.name : 'Every covered company and holding.'}
        description={
          client
            ? 'Coverage as the client sees it, with team detail, mapping exceptions and holdings.'
            : 'Choose a client to see its coverage, mapping exceptions and holdings.'
        }
        actions={
          <div className="w-full sm:w-72">
            <Select
              label="Client"
              hideLabel
              value={org ?? ''}
              placeholder="Choose a client"
              onChange={(event) => setParam('org', event.target.value || null)}
              options={(clients.data?.items ?? []).map((c) => ({
                value: c.id,
                label: `${c.name}${c.open_exceptions ? ` · ${c.open_exceptions} exceptions` : ''}`,
              }))}
            />
          </div>
        }
      />
      {clients.error ? (
        <Callout tone="danger" role="alert">
          {errorMessage(clients.error)}
        </Callout>
      ) : null}
      {!org ? (
        <EmptyState
          icon={<Building />}
          title="Choose a client"
          description="Coverage, holdings and mapping exceptions always belong to one client organisation."
        />
      ) : (
        <Tabs
          key={org}
          label="Coverage sections"
          value={tab}
          onValueChange={(id) => setParam('tab', id === 'coverage' ? null : id)}
          items={[
            {
              id: 'coverage',
              label: 'Coverage',
              content: (
                <CoverageRegister
                  org={org}
                  base="/workspace"
                  staff
                  {...(manager
                    ? {
                        onResolve: (row: CoverageRow) => {
                          setFocusException(row.staff?.open_exception_id ?? null)
                          setParam('tab', 'exceptions')
                        },
                      }
                    : {})}
                />
              ),
            },
            {
              id: 'exceptions',
              label: 'Mapping exceptions',
              ...(client ? { count: client.open_exceptions } : {}),
              content: (
                <MappingExceptionsTab org={org} canResolve={manager} focusId={focusException} />
              ),
            },
            {
              id: 'holdings',
              label: 'Holdings',
              content: <HoldingsTab org={org} canImport={manager} />,
            },
          ]}
        />
      )}
    </>
  )
}

function HoldingsTab({ org, canImport }: { org: string; canImport: boolean }) {
  const [query, setQuery] = useState('')
  const [cursors, setCursors] = useState<string[]>([])
  const holdings = useHoldings(org, { cursor: cursors[cursors.length - 1] ?? null, query })
  const portfolios = usePortfolios(org)
  const importDialog = useDialog()
  return (
    <div className="space-y-4">
      <Card
        title="Portfolios"
        actions={
          canImport ? (
            <Button icon={<Upload />} onClick={() => importDialog.open()}>
              Import holdings CSV
            </Button>
          ) : null
        }
      >
        {portfolios.data?.items.length ? (
          <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {portfolios.data.items.map((p) => (
              <li key={p.id} className="rounded-md border border-line p-3 text-sm">
                <span className="block font-semibold text-ink">{p.name}</span>
                <span className="text-xs text-sage">
                  {p.holdings} holdings · latest position {formatDate(p.latest_as_of, 'none')}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-sage">
            No portfolios yet. Import a holdings file to create them.
          </p>
        )}
      </Card>
      <div className="max-w-md">
        <Input
          label="Search holdings"
          hideLabel
          type="search"
          placeholder="Search identifier or company"
          value={query}
          onChange={(event) => {
            setQuery(event.target.value)
            setCursors([])
          }}
        />
      </div>
      <DataTable
        caption="Holdings"
        rows={holdings.data?.items ?? []}
        getRowId={(h) => h.id}
        loading={holdings.isPending}
        empty={<EmptyState variant="plain" title="No records match this search" />}
        pagination={{
          mode: 'server',
          hasNext: Boolean(holdings.data?.next_cursor),
          hasPrevious: cursors.length > 0,
          onNext: () => {
            const next = holdings.data?.next_cursor
            if (next) setCursors((c) => [...c, next])
          },
          onPrevious: () => setCursors((c) => c.slice(0, -1)),
        }}
        columns={[
          {
            id: 'identifier',
            header: 'Identifier',
            cell: (h) => (
              <div>
                <span className="font-semibold text-ink">{h.identifier_value}</span>
                <span className="block text-xs text-sage">{h.identifier_type}</span>
              </div>
            ),
          },
          { id: 'company', header: 'Company', cell: (h) => h.company ?? h.issuer_name_hint ?? '—' },
          { id: 'portfolio', header: 'Portfolio', cell: (h) => h.portfolio },
          { id: 'as_of', header: 'Position', cell: (h) => formatDate(h.as_of) },
          {
            id: 'weight',
            header: 'Weight %',
            align: 'right',
            cell: (h) =>
              h.weight === null
                ? '—'
                : h.weight.toLocaleString('en-GB', { maximumFractionDigits: 4 }),
          },
          {
            id: 'mapping',
            header: 'Mapping',
            cell: (h) => <StatusBadge status={h.mapping_state} />,
          },
        ]}
      />
      <ImportDialog open={importDialog.isOpen} onClose={importDialog.close} org={org} />
    </div>
  )
}

/** S4 staff variant: `/workspace/companies/:id?org=` (Fruuit-wide without `org`). */
export function WorkspaceCompanyPage() {
  const { id = '' } = useParams()
  const [params] = useSearchParams()
  return (
    <CompanyDossierPage
      issuerId={id}
      base="/workspace"
      org={params.get('org')}
      canRecordActions={false}
    />
  )
}
